import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { iif, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { SellPreviewRestService, SellRestService, decrementPublicCount, incrementPublicCounter } from 'sp-arch-lib';
import * as redirectMessageActions from 'sp-mark/feature-modules/redirect-message/redux/redirect-message.actions';
import { routingPathsConstants } from 'sp-mark/models/routing-paths.constants';
import * as concludeSellingActions from 'sp-mark/redux/conclude-selling/conclude-selling.actions';
import * as paymentConclusionSelectors from 'sp-mark/redux/payment-conclusion/index';
import * as paymentConclusionActions from 'sp-mark/redux/payment-conclusion/payment-conclusion.actions';
import * as routingActions from 'sp-mark/redux/router/router.actions';

@Injectable()
export class PaymntConclusionEffects {
  retrieveSellSellPrev$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.retrieveSell),
      filter(action => action.operationContext === 'sell-preview'),
      switchMap(action =>
        this.sellPreviewService.retrieveOperationInfo(action.idOperation, action.processCompleted).pipe(
          switchMap(operationResponse =>
            iif(
              () => !!operationResponse,
              of(
                paymentConclusionActions.retrieveSellSuccess({
                  operation: {
                    mac: operationResponse.mac,
                    alias: operationResponse.alias,
                    group: operationResponse.group,
                    channel: operationResponse.channel,
                    amount: operationResponse.amount,
                    totalInstallments: operationResponse.totalInstallments,
                    serviceName: operationResponse.serviceName,
                    contract: operationResponse.contract,
                    operationId: operationResponse.operationId,
                    internalOperationId: operationResponse.internalOperationId,
                    posTransaction: operationResponse.posTransaction,
                    externalPayment: operationResponse.externalPayment,
                    flagPerpetual: operationResponse.flagPerpetual,
                    lastDatePayableBy: operationResponse.lastDatePayableBy,
                    payer: {
                      id: operationResponse.payer.id,
                      firstName: operationResponse.payer.firstName,
                      lastName: operationResponse.payer.lastName,
                      phoneNumber: operationResponse.payer.phoneNumber,
                      eMailAddress: operationResponse.payer.eMailAddress
                    }
                  }
                })
              ),
              of(paymentConclusionActions.retrieveSellFailure())
            )
          ),
          catchError(() => of(paymentConclusionActions.retrieveSellFailure()))
        )
      )
    )
  );

  retrieveSellConcludeSelling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.retrieveSell),
      filter(action => action.operationContext === 'conclude-selling'),
      switchMap(action =>
        this.sellPreviewService.retrieveOperationInfo(action.idOperation, action.processCompleted).pipe(
          switchMap(operationResponse => {
            if (!!operationResponse) {
              let totalInstallments = null;
              if (!!operationResponse.multipleInstallments && Object.keys(operationResponse.multipleInstallments).length > 0) {
                totalInstallments = Object.keys(operationResponse.multipleInstallments)[0];
              }
              const actionsToReturn = [
                paymentConclusionActions.retrieveSellSuccess({
                  operation: {
                    mac: operationResponse.mac,
                    alias: operationResponse.alias,
                    group: operationResponse.group,
                    channel: operationResponse.channel,
                    amount: operationResponse.amount,
                    totalInstallments: operationResponse.totalInstallments,
                    serviceName: operationResponse.serviceName,
                    contract: operationResponse.contract,
                    operationId: operationResponse.operationId,
                    internalOperationId: operationResponse.internalOperationId,
                    installments: operationResponse.installments,
                    multipleInstallments: operationResponse.multipleInstallments,
                    flagAdaptInstallments: operationResponse.flagAdaptInstallments,
                    redirectUrl: operationResponse.redirectUrl,
                    posTransaction: operationResponse.posTransaction,
                    externalPayment: operationResponse.externalPayment,
                    flagPerpetual: operationResponse.flagPerpetual,
                    lastDatePayableBy: operationResponse.lastDatePayableBy,
                    payer: {
                      firstName: operationResponse.payer.firstName,
                      lastName: operationResponse.payer.lastName,
                      phoneNumber: operationResponse.payer.phoneNumber,
                      eMailAddress: operationResponse.payer.eMailAddress,
                      fiscalCode: operationResponse.payer.fiscalCode
                    }
                  }
                }),
                paymentConclusionActions.updateTotalInstallmentsAction({totalInstallments}),
                redirectMessageActions.storeSellResponse({
                  sellResponse: {
                    mac: operationResponse.mac,
                    alias: operationResponse.alias,
                    group: operationResponse.group,
                    channel: operationResponse.channel,
                    amount: operationResponse.amount,
                    totalInstallments: operationResponse.totalInstallments,
                    contract: operationResponse.contract,
                    operationId: operationResponse.operationId,
                    installments: operationResponse.installments,
                    multipleInstallments: operationResponse.multipleInstallments,
                    flagAdaptInstallments: operationResponse.flagAdaptInstallments,
                    redirectUrl: operationResponse.redirectUrl,
                    externalPayment: operationResponse.externalPayment,
                    payer: {
                      firstName: operationResponse.payer.firstName,
                      lastName: operationResponse.payer.lastName,
                      phoneNumber: operationResponse.payer.phoneNumber,
                      eMailAddress: operationResponse.payer.eMailAddress,
                      fiscalCode: operationResponse.payer.fiscalCode
                    }
                  }
                }),
                redirectMessageActions.storeSellResponseStatus({
                  sellResponse: {
                    mac: operationResponse.mac,
                    alias: operationResponse.alias,
                    group: operationResponse.group,
                    amount: operationResponse.amount,
                    totalInstallments: operationResponse.totalInstallments,
                    serviceName: operationResponse.serviceName,
                    actionRequired: operationResponse.actionRequired,
                    contract: operationResponse.contract,
                    channel: operationResponse.channel,
                    internalOperationId: operationResponse.internalOperationId,
                    operationId: operationResponse.operationId,
                    result: operationResponse.state === 'PAGATO' ? 'OK' : 'KO',
                    message: operationResponse.message,
                    redirectUrl: operationResponse.redirectUrl,
                    posTransaction: operationResponse.posTransaction,
                    state: operationResponse.state,
                    externalPayment: operationResponse.externalPayment,
                    flagPerpetual: operationResponse.flagPerpetual,
                    payer: {
                      firstName: operationResponse.payer.firstName,
                      lastName: operationResponse.payer.lastName,
                      phoneNumber: operationResponse.payer.phoneNumber,
                      eMailAddress: operationResponse.payer.eMailAddress,
                      fiscalCode: operationResponse.payer.fiscalCode
                    }
                  }
                })
              ];
              return actionsToReturn;
            }

            return of(paymentConclusionActions.retrieveSellFailure());
          }),
          catchError(() => of(paymentConclusionActions.retrieveSellFailure()))
        )
      )
    )
  );

  /**
   * esegue il pagamento e ridispatcha handleExecutePaymentResponse, che deve essere gestita nei
   * singoli moduli di sell-preview e conclude-selling
   */
  executePayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.executePayment),
      switchMap(action =>
        this.sellPreviewService.executePayment({...action.payment}).pipe(
          map(sellResponse => paymentConclusionActions.handleExecutePaymentResponse({sellResponse})),
          catchError(() => [decrementPublicCount(), paymentConclusionActions.executePaymentFailure()])
        )
      )
    )
  );

  /**
   * cambio metodo di pagamento
   */
  attachPaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.attachPaymentMethod),
      tap(() => this.appState$.dispatch(incrementPublicCounter())),
      switchMap(action =>
        this.sellPreviewService.attachPaymentMethod({...action.payment}).pipe(
          map(response => paymentConclusionActions.handleExecutePaymentResponse({
              sellResponse: {
                result: response.result,
                message: response.message
              }
            })
          ),
          catchError(() => [decrementPublicCount(), paymentConclusionActions.executePaymentFailure()])
        )
      )
    )
  );

  handleExecutePaymentResponseActionNotRequired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.handleExecutePaymentResponse),
      filter(action => !action.sellResponse.actionRequired),
      map(action => action.sellResponse),
      switchMap(sellResponse => {
        const actionsToReturn: Action[] = [paymentConclusionActions.actionRequiredOnPayment({actionRequired: false})];
        switch (sellResponse.result) {
          case 'PEN':
          case 'OK':
            actionsToReturn.push(concludeSellingActions.executePaymentConcluded({sellResponse}));
            break;
          case 'REDIRECT':
            actionsToReturn.push(concludeSellingActions.manageRedirect({sellResponse}));
            break;
          case 'KO':
          default:
            actionsToReturn.push(concludeSellingActions.executePaymentFailure());
            break;
        }
        return actionsToReturn;
      })
    )
  );

  handleExecutePaymentResponseActionRequired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.handleExecutePaymentResponse),
      filter(action => action.sellResponse.actionRequired),
      map(() => paymentConclusionActions.actionRequiredOnPayment({actionRequired: true})),
    )
  );

  manageRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(concludeSellingActions.manageRedirect),
        map(action => action.sellResponse),
        map(sellResponse => (document.location.href = sellResponse.redirectUrl))
      ),
    {dispatch: false}
  );

  /**
   * esegue il pagamento con rate e ridispatcha handleExecutePaymentResponse, che deve essere gestita nei
   * singoli moduli di sell-preview e conclude-selling
   */
  executePaymentWithNewInstallments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.executePaymentWithNewInstallments),
      tap(() => this.appState$.dispatch(incrementPublicCounter())),
      withLatestFrom(
        this.appState$.select(paymentConclusionSelectors.selectCardDetails),
        this.appState$.select(paymentConclusionSelectors.selectOperation)
      ),
      map(([action, cardDetails, operation]) => ({
        contract: operation.contract,
        idOperation: operation.operationId,
        pan: cardDetails.pan,
        scadenza: `${cardDetails.expiryMonth}/${cardDetails.expiryYear}`,
        xpayNonce: cardDetails.xPayNonce,
        totalInstallments: operation.installments.length - 1,
        payer: action.payer
      })),
      switchMap(payload =>
        this.sellPreviewService.executePayment(payload).pipe(
          map(sellResponse => paymentConclusionActions.handleExecutePaymentResponse({sellResponse})),
          catchError(() => [decrementPublicCount(), paymentConclusionActions.executePaymentFailure()])
        )
      )
    )
  );

  executePaymentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.executePaymentSuccess),
      switchMap(() => [routingActions.navigateTo(routingPathsConstants.CONGRATS), decrementPublicCount()])
    )
  );

  executePaymentKo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.executePaymentKo),
      switchMap(() => [routingActions.navigateTo(routingPathsConstants.PAYMENTKO), decrementPublicCount()])
    )
  );

  listenToSellingState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.listenToSellingState),
      switchMap(action =>
        this.sellRestService.listenToSellingState(action.paymentId).pipe(
          switchMap(operationResponse =>
            iif(
              () => !!operationResponse,
              of(paymentConclusionActions.listenToSellingStateResponse()),
              of(paymentConclusionActions.listenToSellingStateError())
            )
          ),
          catchError(() => of(paymentConclusionActions.listenToSellingStateError()))
        )
      )
    )
  );

  listenToSellingStateResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentConclusionActions.listenToSellingStateResponse),
      map(() => routingActions.navigateTo(routingPathsConstants.DASHBOARD))
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly sellPreviewService: SellPreviewRestService,
    private appState$: Store,
    private readonly sellRestService: SellRestService
  ) {
  }
}
